import { useState, useEffect } from 'react';
import { useRecoilValue, useRecoilState, useSetRecoilState } from 'recoil';
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerCloseButton,
  DrawerOverlay,
  DrawerHeader,
  DrawerFooter,
  Modal,
  ModalOverlay,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalCloseButton,
  VStack,
  HStack,
  Icon,
  Text,
  Center,
  Button,
  useDisclosure,
} from '@chakra-ui/react';
import LocationItem from './LocationItem';
import ClearCartConfirmModal from '~/components/ClearCartConfirmModal';

import remotePickMealPointsListAtom from '~/recoil/atom/remotePickMealPointsList';
import remotePickMealPointAtom from '~/recoil/atom/remotePickMealPoint';
import mediaQueryStateAtom from '~/recoil/atom/mediaQueryState';
import pickMealEndAtom from '~/recoil/atom/pickMealEnd';
import pickMealAtAtom from '~/recoil/atom/pickMealAt';
import platformAtom from '~/recoil/atom/platform';
import ordersAtom from '~/recoil/atom/orders';

import useCartId from '~/hooks/useCartId';

import { RiMapPinLine } from 'react-icons/ri';

import * as fp from 'lodash/fp';

import updateCart from '~/firebase/update/updateCart';

import getRecentPickMealTime from '~/utils/getRecentPickMealTime';

import { PLATFORM_ID } from '~/globalVariables';

import { RemotePickMealPointWithId } from '~/types/platform/remotePickMealPoint';

export type LocationSelectorModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export default function LocationSelectorModal({ isOpen, onClose }: LocationSelectorModalProps) {
  const { isLargerThan768 } = useRecoilValue(mediaQueryStateAtom);
  const platform = useRecoilValue(platformAtom);
  const setPickMealAt = useSetRecoilState(pickMealAtAtom);
  const setPickMealEnd = useSetRecoilState(pickMealEndAtom);
  const [orders, setOrders] = useRecoilState(ordersAtom);

  const { cartId } = useCartId();

  const remotePickMealPointsList = useRecoilValue(remotePickMealPointsListAtom);
  const [remotePickMealPoint, setRemotePickMealPoint] = useRecoilState(remotePickMealPointAtom);

  const {
    isOpen: isConfirmModalOpen,
    onOpen: onConfirmModalOpen,
    onClose: onConfirmModalClose,
  } = useDisclosure();

  // default point id
  const [selectedPickMealPoint, setPickMealPoint] = useState(
    remotePickMealPointsList.find((point) => fp.eq(point.id, remotePickMealPoint?.id)) || null,
  );

  const handleSelectPoint = (point: RemotePickMealPointWithId) => {
    setPickMealPoint(point);
  };

  useEffect(() => {
    if (!isOpen) {
      setPickMealPoint(
        remotePickMealPointsList.find((point) => fp.eq(point.id, remotePickMealPoint?.id)) || null,
      );
    }
  }, [isOpen, remotePickMealPointsList, remotePickMealPoint]);

  const handleChangePickMealPoint = async (pickMealPoint: RemotePickMealPointWithId | null) => {
    const cartPickMealPoint = pickMealPoint
      ? {
          id: pickMealPoint.id,
          name: pickMealPoint.name,
          address: pickMealPoint.location.address,
          geoPoint: pickMealPoint.location.geoPoint,
        }
      : null;

    const cartPickMealTime = pickMealPoint
      ? getRecentPickMealTime(platform.preOrderSetting, pickMealPoint.operatingTime, new Date())
      : {
          at: null,
          end: null,
        };

    if (!cartId) {
      setRemotePickMealPoint(cartPickMealPoint);
      setPickMealAt(cartPickMealTime.at);
      setPickMealEnd(cartPickMealTime.end);
      setOrders([]);
      onConfirmModalClose();
      onClose();
      return;
    }

    try {
      await updateCart(PLATFORM_ID, cartId, {
        pickMealPoint: cartPickMealPoint,
        pickMealAt: cartPickMealTime.at,
        pickMealEnd: cartPickMealTime.end,
        orders: [],
      });
      onConfirmModalClose();
      onClose();
    } catch (err) {
      console.error('update cart error', err);
    }
  };

  const handleConfirmClick = async () => {
    if (fp.eq(selectedPickMealPoint?.id, remotePickMealPoint?.id)) {
      onClose();
      return;
    }

    if (fp.lte(orders.length, 0)) {
      handleChangePickMealPoint(selectedPickMealPoint);
      return;
    }

    onConfirmModalOpen();
  };

  return (
    <>
      {isLargerThan768 ? (
        <Modal isOpen={isOpen} onClose={onClose} size="md" scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent m={10} h="95%">
            <ModalHeader borderBottom="1px solid" borderBottomColor="primary">
              <Center mt={4}>
                <HStack spacing={3}>
                  <Icon as={RiMapPinLine} color="primary" boxSize="1.5rem" />
                  <Text fontSize="xl" fontWeight="normal">
                    選擇取餐地點
                  </Text>
                </HStack>
              </Center>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody p={0}>
              <VStack p={6} align="stretch">
                {remotePickMealPointsList.map((point) => {
                  return (
                    <LocationItem
                      key={point.id}
                      pointData={point}
                      isActive={fp.eq(point.id, selectedPickMealPoint?.id)}
                      onClick={() => handleSelectPoint(point)}
                      onClose={onClose}
                    />
                  );
                })}
              </VStack>
            </ModalBody>
            <ModalFooter p={0}>
              <Button
                h="3.5rem"
                onClick={handleConfirmClick}
                variant="unstyled"
                bg="primary"
                borderRadius="unset"
                color="white"
                isFullWidth
              >
                確認
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      ) : (
        <Drawer isOpen={isOpen} onClose={onClose} placement="left" size="xl">
          <DrawerOverlay />
          <DrawerContent h="full">
            <DrawerHeader borderBottom="1px solid" borderBottomColor="primary">
              <Center mt={4}>
                <HStack spacing={3}>
                  <Icon as={RiMapPinLine} color="primary" boxSize="1.5rem" />
                  <Text fontSize="xl" fontWeight="normal">
                    選擇取餐地點
                  </Text>
                </HStack>
              </Center>
            </DrawerHeader>
            <DrawerCloseButton />
            <DrawerBody>
              <VStack align="stretch" py={6}>
                {remotePickMealPointsList.map((point) => {
                  return (
                    <LocationItem
                      key={point.id}
                      pointData={point}
                      isActive={fp.eq(point.id, selectedPickMealPoint?.id)}
                      onClick={() => handleSelectPoint(point)}
                      onClose={onClose}
                    />
                  );
                })}
              </VStack>
            </DrawerBody>
            <DrawerFooter p={0}>
              <Button
                h="3.5rem"
                onClick={handleConfirmClick}
                variant="unstyled"
                bg="primary"
                borderRadius="unset"
                color="white"
                isFullWidth
              >
                確認
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      )}

      <ClearCartConfirmModal
        isOpen={isConfirmModalOpen}
        title="確定改變取餐地點？"
        onClose={onConfirmModalClose}
        onConfirm={() => handleChangePickMealPoint(selectedPickMealPoint)}
      >
        <VStack align="center">
          <Text textAlign="center" fontSize="sm" color="danger.600">
            改變取餐地點，將同時改變您的購物車
          </Text>
          <Text textAlign="center" fontSize="sm" color="gray.400">
            ※每個取餐地點有屬於自己的購物車
          </Text>
        </VStack>
      </ClearCartConfirmModal>
    </>
  );
}
